import type { ComponentType } from "react";
import { createBrowserRouter } from "react-router";
import { AppShell, HouseholdLayout } from "./routes/AppShell";
import { NotFoundPage, RouteErrorPage } from "./routes/errors";
import { HomeRedirect, RequireHousehold, RequireUser, RequireVerified } from "./routes/guards";
import { Root, Splash } from "./routes/Root";
import { AuthActionPage } from "./routes/auth/AuthAction";
import { DevOutboxPage } from "./routes/auth/DevOutbox";
import { ForgotPasswordPage } from "./routes/auth/ForgotPassword";
import { LoginPage } from "./routes/auth/Login";
import { RegisterPage } from "./routes/auth/Register";
import { VerifyEmailPage } from "./routes/auth/VerifyEmail";
import { JoinPage } from "./routes/Join";
import { OnboardingPage } from "./routes/Onboarding";

/** Route-level code splitting: each feature area loads on first visit (and is precached for offline). */
function page<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M & string) {
  return async () => ({ Component: (await load())[name] as ComponentType });
}

/**
 * Route map (single owner: frontend lead). Feature pages live in routes/<area>/ and are owned by
 * their area; see .agent-work/frontend-status.md.
 */
export const router = createBrowserRouter([
  /*
   * Public pages sit OUTSIDE <Root> so they render without /api/config and without Firebase Auth:
   * the marketing site and the help page must stay readable when the API or auth is unreachable.
   * The signed-in entry point moved from "/" to "/app" (still behind Root).
   */
  {
    path: "/",
    errorElement: <RouteErrorPage />,
    hydrateFallbackElement: <Splash />,
    lazy: page(() => import("./routes/public/Landing"), "LandingPage"),
  },
  {
    path: "/help",
    errorElement: <RouteErrorPage />,
    hydrateFallbackElement: <Splash />,
    lazy: page(() => import("./routes/public/Help"), "HelpPage"),
  },
  // The policy pages are public for the same reason: they must be readable without an account and
  // without the API, including from the registration form that asks you to agree to them.
  {
    path: "/privacy",
    errorElement: <RouteErrorPage />,
    hydrateFallbackElement: <Splash />,
    lazy: page(() => import("./routes/public/Legal"), "PrivacyPage"),
  },
  {
    path: "/terms",
    errorElement: <RouteErrorPage />,
    hydrateFallbackElement: <Splash />,
    lazy: page(() => import("./routes/public/Legal"), "TermsPage"),
  },
  {
    element: <Root />,
    errorElement: <RouteErrorPage />,
    hydrateFallbackElement: <Splash />,
    children: [
      { path: "app", element: <HomeRedirect /> },
      { path: "login", element: <LoginPage /> },
      { path: "register", element: <RegisterPage /> },
      { path: "forgot-password", element: <ForgotPasswordPage /> },
      { path: "auth/action", element: <AuthActionPage /> },
      { path: "dev/outbox", element: <DevOutboxPage /> },
      {
        element: <RequireUser />,
        children: [
          { path: "verify-email", element: <VerifyEmailPage /> },
          {
            element: <RequireVerified />,
            children: [
              { path: "oauth/consent", lazy: page(() => import("./routes/auth/OAuthConsent"), "OAuthConsentPage") },
              { path: "welcome", element: <OnboardingPage /> },
              { path: "join/:code", element: <JoinPage /> },
              {
                element: <RequireHousehold />,
                children: [
                  {
                    element: <HouseholdLayout />,
                    children: [
                      { path: "cook/:recipeId", lazy: page(() => import("./routes/cook/CookMode"), "CookPage") },
                      { path: "recipes/:id/print", lazy: page(() => import("./routes/recipes/PrintRecipe"), "PrintRecipePage") },
                      {
                        element: <AppShell />,
                        errorElement: <RouteErrorPage />,
                        children: [
                          { path: "recipes", lazy: page(() => import("./routes/recipes/RecipeList"), "RecipeListPage") },
                          { path: "recipes/new", lazy: page(() => import("./routes/recipes/RecipeEditor"), "RecipeEditorPage") },
                          { path: "recipes/import", lazy: page(() => import("./routes/ai/ImportRecipe"), "ImportRecipePage") },
                          { path: "recipes/ask", lazy: page(() => import("./routes/ai/AskRecipes"), "AskRecipesPage") },
                          { path: "recipes/trash", lazy: page(() => import("./routes/recipes/Trash"), "TrashPage") },
                          { path: "recipes/categories", lazy: page(() => import("./routes/recipes/Categories"), "CategoriesPage") },
                          { path: "recipes/:id", lazy: page(() => import("./routes/recipes/RecipeDetail"), "RecipeDetailPage") },
                          { path: "recipes/:id/edit", lazy: page(() => import("./routes/recipes/RecipeEditor"), "RecipeEditorPage") },
                          { path: "cook", lazy: page(() => import("./routes/cook/CookMode"), "CookIndexPage") },
                          { path: "groceries", lazy: page(() => import("./routes/groceries/GroceryLists"), "GroceryListsPage") },
                          { path: "groceries/:listId", lazy: page(() => import("./routes/groceries/GroceryListPage"), "GroceryListPage") },
                          { path: "pantry", lazy: page(() => import("./routes/pantry/Pantry"), "PantryPage") },
                          { path: "plan", lazy: page(() => import("./routes/plan/Plan"), "PlanPage") },
                          { path: "plan/draft", lazy: page(() => import("./routes/ai/DraftPlan"), "DraftPlanPage") },
                          { path: "plan/menus", lazy: page(() => import("./routes/plan/Menus"), "MenusPage") },
                          { path: "plan/menus/new", lazy: page(() => import("./routes/plan/MenuEditor"), "MenuEditorPage") },
                          { path: "plan/menus/:menuId", lazy: page(() => import("./routes/plan/MenuEditor"), "MenuEditorPage") },
                          { path: "settings", lazy: page(() => import("./routes/settings/Settings"), "SettingsPage") },
                          { path: "settings/members", lazy: page(() => import("./routes/settings/Members"), "MembersPage") },
                          { path: "settings/api", lazy: page(() => import("./routes/settings/ApiAccess"), "ApiAccessPage") },
                          { path: "*", element: <NotFoundPage /> },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
